/**
 * Shabllonet Word — analiza në shfletues dhe ruajtja te serveri.
 *
 * Dokumenti mbetet .docx; këtu vetëm gjenden vendmbajtësit e tij ({!emri!},
 * {{emri}}, …) dhe propozohet fusha për secilin. Teksti HTML (`html`) ruhet
 * vetëm si rezervë për listën dhe kërkimin — gjenerimi përdor Word-in.
 */
import { authFetch } from '../services/apiServices';
import { docxToHtml } from './docxImport';
import { detectSlots, applySlots } from './templateImport';

/**
 * @returns {Promise<{ html: string, tags: {key,raw,suggestion}[], fieldMap: object }>}
 */
export async function analyzeWordFile(file) {
  const raw = await docxToHtml(file);
  const { html: base, slots } = detectSlots(raw);
  const placeholders = slots.filter((s) => s.key);
  const seen = new Map();
  placeholders.forEach((s) => {
    if (!seen.has(s.key)) seen.set(s.key, { key: s.key, raw: s.placeholder, suggestion: s.suggestion });
  });
  const tags = [...seen.values()];
  const fieldMap = Object.fromEntries(tags.filter((t) => t.suggestion).map((t) => [t.key, t.suggestion]));
  // Rezerva HTML: vendmbajtësit e njohur bëhen @Fusha, vijat bosh mbeten.
  const html = applySlots(base, placeholders, Object.fromEntries(placeholders.map((s) => [s.id, s.suggestion])));
  return { html, tags, fieldMap };
}

/** Ruan dokumentin dhe hartën e fushave te shablloni. */
export async function uploadTemplateDocx(templateId, file, fieldMap) {
  const fd = new FormData();
  fd.append('document', file, file.name || 'shabllon.docx');
  fd.append('fieldMap', JSON.stringify(fieldMap || {}));
  const res = await authFetch(`/api/contract-templates/${templateId}/docx`, { method: 'PUT', body: fd });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.error || 'Dokumenti Word nuk u ruajt.');
  return json.data;
}

/** Dokumenti i ruajtur si File — që faqja e redaktimit ta trajtojë si një të sapongarkuar. */
export async function fetchTemplateDocx(templateId, name) {
  const res = await authFetch(`/api/contract-templates/${templateId}/docx`);
  if (!res.ok) return null;
  const blob = await res.blob();
  return new File([blob], name || 'shabllon.docx', { type: blob.type });
}

export async function removeTemplateDocx(templateId) {
  await authFetch(`/api/contract-templates/${templateId}/docx`, { method: 'DELETE' });
}

/** Emri i skedarit nga Content-Disposition — `filename*` (UTF-8) para `filename`. */
function fileNameFrom(res, fallback) {
  const cd = res.headers.get('Content-Disposition') || '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) { try { return decodeURIComponent(star[1]); } catch { /* bie te tjetri */ } }
  const plain = /filename="([^"]+)"/i.exec(cd);
  return plain ? plain[1] : fallback;
}

/**
 * Kontrata e plotësuar e një punëtori (Word ose PDF).
 * `open: true` e hap PDF-në në tab të ri (për printim); përndryshe shkarkohet
 * me emrin e punëtorit — «Kontrata_Fisnik_Sejdiu.pdf», jo ID-ja e blob-it.
 */
export async function downloadContractDocument(contractId, format = 'docx', templateId, { open = false } = {}) {
  const qs = new URLSearchParams({ format });
  if (templateId) qs.set('templateId', String(templateId));
  const res = await authFetch(`/api/worker-contracts/${contractId}/document?${qs.toString()}`);
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.error || 'Kontrata nuk u gjenerua.');
  }
  const blob = await res.blob();
  const name = fileNameFrom(res, `Kontrata.${format}`);
  const url = URL.createObjectURL(blob);
  if (open && format === 'pdf') {
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  }
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
